import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trophy, Sparkles, Zap, BarChart3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import AISafetyCard from "@/components/AISafetyCard";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { colorFor, TX_COLORS } from "@/lib/reportColors";
import Confetti from "./_ganador/Confetti";
import PodiumStep from "./_ganador/PodiumStep";
import MiniPodium from "./_ganador/MiniPodium";
import { EXPECTED_TX_TYPES } from "./_ganador/medalStyles";

export default function Ganador() {
  const { t } = useTranslation();
  const { filters, setFilters, reset, payload } = useFilters();
  const [period, setPeriod] = useState("month");
  const [anchor, setAnchor] = useState("");
  const [mode, setMode] = useState("volume");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Stringify the filter payload so referential changes don't trigger needless
  // refetches (intentional — payload identity changes on every render).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    reports
      .ganador(payload, period, anchor || null, mode)
      .then((r) => alive && setData(r.data))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // payload tracked indirectly via payloadKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, period, anchor, mode]);

  const winner = data?.winner;
  const podium = data?.podium || [];
  const accent = winner?.department_color || (mode === "efficiency" ? "#BB9AF7" : "#E0AF68");

  return (
    <div className="space-y-6" data-testid="ganador-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="ganador-title">
          {t("ganador.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("ganador.subtitle")}</p>
      </div>

      <GlobalFilters filters={filters} onChange={setFilters} onReset={reset} />

      {/* Mode + Period selector */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("ganador.mode")}</Label>
          <div
            className="inline-flex h-9 items-center rounded-md border border-border bg-card p-0.5"
            data-testid="ganador-mode-toggle"
          >
            <Button
              type="button"
              variant={mode === "volume" ? "default" : "ghost"}
              size="sm"
              onClick={() => setMode("volume")}
              data-testid="ganador-mode-volume"
              className="h-8 gap-1.5 rounded px-3 text-xs"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              {t("ganador.modeVolume")}
            </Button>
            <Button
              type="button"
              variant={mode === "efficiency" ? "default" : "ghost"}
              size="sm"
              onClick={() => setMode("efficiency")}
              data-testid="ganador-mode-efficiency"
              className="h-8 gap-1.5 rounded px-3 text-xs"
            >
              <Zap className="h-3.5 w-3.5" />
              {t("ganador.modeEfficiency")}
            </Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("ganador.period")}</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="ganador-period-select" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["day", "week", "month", "year"].map((p) => (
                <SelectItem key={p} value={p} data-testid={`ganador-period-${p}`}>
                  {t(`ganador.${p}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("ganador.anchor")}</Label>
          <Input
            value={anchor}
            onChange={(e) => setAnchor(e.target.value)}
            placeholder={t("ganador.anchorPlaceholder")}
            data-testid="ganador-anchor-input"
            className="h-9 font-mono text-xs"
          />
        </div>
      </div>

      {/* Winner XXL card */}
      <Card
        data-testid="ganador-winner-card"
        className="relative overflow-hidden border-2"
        style={{ borderColor: `${accent}55` }}
      >
        {winner && <Confetti />}
        <CardContent className="p-8">
          {!winner ? (
            <div className="py-16 text-center text-muted-foreground" data-testid="ganador-empty">
              {loading ? t("common.loading") : t("ganador.noData")}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-stretch">
              <div className="relative flex-shrink-0">
                <div
                  className="flex h-32 w-32 items-center justify-center rounded-full text-5xl font-semibold text-white shadow-xl"
                  style={{ background: accent }}
                  data-testid="ganador-avatar"
                >
                  {winner.photo_initial}
                </div>
                <Trophy
                  className="absolute -right-2 -top-2 h-10 w-10 rounded-full border-4 border-background bg-yellow-400 p-1.5 text-yellow-900 shadow-md"
                  data-testid="ganador-trophy-icon"
                />
              </div>
              <div className="flex flex-col justify-center text-center sm:text-left">
                <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <Sparkles className="h-3 w-3" />
                  {t("ganador.winnerLabel")} · {data.period_label}
                </div>
                <div
                  className="text-4xl font-semibold leading-tight tracking-tight"
                  data-testid="ganador-name"
                >
                  {winner.employee_name}
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  <Badge variant="outline" className="font-mono text-xs">
                    {winner.employee_id}
                  </Badge>
                  {winner.department_name && (
                    <Badge style={{ background: `${accent}33`, color: accent, borderColor: accent }}>
                      {winner.department_name}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-center justify-center sm:ml-auto sm:items-end">
                <div
                  className="text-6xl font-bold tabular-nums"
                  style={{ color: accent }}
                  data-testid="ganador-total"
                >
                  {mode === "efficiency"
                    ? (winner.tx_per_hour != null ? winner.tx_per_hour.toFixed(2) : "—")
                    : winner.total.toLocaleString()}
                </div>
                <div className="text-sm uppercase tracking-wider text-muted-foreground">
                  {mode === "efficiency"
                    ? t("ganador.txPerHourLabel")
                    : t("ganador.totalTransactions")}
                </div>
                {mode === "efficiency" && (
                  <div
                    className="mt-1 text-xs text-muted-foreground"
                    data-testid="ganador-eff-detail"
                  >
                    {winner.total?.toLocaleString?.() || winner.total} tx ·{" "}
                    {(winner.hours_worked ?? 0).toFixed(2)} h
                  </div>
                )}
                {/* By-type chips */}
                <div className="mt-3 flex flex-wrap gap-1">
                  {Object.entries(winner.by_type || {}).map(([k, v]) => (
                    <Badge
                      key={k}
                      variant="secondary"
                      className="text-[10px] font-mono"
                      style={{ background: `${TX_COLORS[k] || colorFor(k)}22` }}
                    >
                      {k}: {v}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Podium */}
      {podium.length > 0 && (
        <Card data-testid="ganador-podium">
          <CardHeader>
            <CardTitle className="text-base">{t("ganador.podium")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-6 px-4 pb-3 pr-8 pt-10">
              <PodiumStep rank={2} person={podium[1]} mode={mode} />
              <PodiumStep rank={1} person={podium[0]} mode={mode} />
              <PodiumStep rank={3} person={podium[2]} mode={mode} />
            </div>
            <div className="mt-4 text-center text-xs text-muted-foreground" data-testid="ganador-eligible">
              {t("ganador.eligible")}: {data.all_eligible}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Safety / Productivity card */}
      <AISafetyCard defaultCategory="safety" testIdPrefix="ganador-ai-msg" />

      {/* Top 3 by transaction type */}
      {data?.top_by_type && (
        <Card data-testid="ganador-top-by-type">
          <CardHeader>
            <CardTitle className="text-base">{t("ganador.topByType")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-5 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
              {EXPECTED_TX_TYPES.map((txType) => {
                const items = data.top_by_type[txType] || [];
                const typeAccent = TX_COLORS[txType] || colorFor(txType);
                return (
                  <div
                    key={txType}
                    className="min-h-[420px] min-w-[280px] rounded-lg border border-border bg-card p-6"
                    data-testid={`top-type-${txType.replace(/[ .]/g, "-")}`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ background: typeAccent }}
                      />
                      <span
                        className="text-xs font-semibold font-mono"
                        style={{ color: typeAccent }}
                      >
                        {txType}
                      </span>
                    </div>
                    {items.length === 0 ? (
                      <div className="flex h-32 items-center justify-center text-center text-[11px] text-muted-foreground">
                        {t("ganador.noPodiumForType")}
                      </div>
                    ) : (
                      <MiniPodium items={items} accentColor={typeAccent} />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
